import type { JSONContent } from '@tiptap/react';
import { diffChars, diffWords, type Change } from 'diff';
import {
  ECodeBlockType,
  EDiffStatus,
  EDiffMark,
  type IBlockDiff,
} from '../text-diffs/utils/diff-types';
import { computeStructuredDiff } from '../text-diffs/utils/diff-utils';

// =============================================================================
// Constants
// =============================================================================

/** Maps non-list block types to TipTap node types */
const BLOCK_TYPE_TO_NODE: Record<
  ECodeBlockType.HEADING | ECodeBlockType.PARAGRAPH,
  string
> = {
  [ECodeBlockType.HEADING]: 'heading',
  [ECodeBlockType.PARAGRAPH]: 'paragraph',
};

/** Regex pattern for parsing bold markdown */
const BOLD_PATTERN = /\*\*([^*]+?)\*\*/g;

// =============================================================================
// Text Node Helpers
// =============================================================================

/**
 * TipTap represents text as nodes with an array of marks for formatting.
 * This helper constructs the proper node structure and marks.
 *
 * @param text - The text content for the node
 * @param marks - Array of mark objects (e.g., { type: 'bold' }, { type: 'diffAdded' })
 * @returns A TipTap JSONContent text node
 *
 * @example
 * createTextNode('hello', [{ type: 'bold' }])
 * // Returns: { type: 'text', text: 'hello', marks: [{ type: 'bold' }] }
 */
function createTextNode(
  text: string,
  marks: Array<{ type: string }> = [],
): JSONContent {
  const node: JSONContent = { type: 'text', text };
  if (marks.length > 0) node.marks = marks;
  return node;
}

/**
 * Parses inline markdown formatting and applies diff marks to the resulting nodes.
 *
 * Currently supports bold markdown (**text**). The diff mark is applied to all
 * generated nodes, allowing text to be both bold AND marked as added/deleted.
 *
 * TODO: Add support for italic, code, and strikethrough if AOP will have them.
 *
 * @param text - The text content that may contain markdown formatting
 * @param diffMark - Optional diff mark to apply (EDiffMark.ADDED or EDiffMark.DELETED)
 * @returns Array of TipTap text nodes with appropriate marks
 *
 * @example
 * parseInlineMarkdown('Hello **world**', EDiffMark.ADDED)
 * // Returns: [
 * //   { type: 'text', text: 'Hello ', marks: [{ type: 'diffAdded' }] },
 * //   { type: 'text', text: 'world', marks: [{ type: 'bold' }, { type: 'diffAdded' }] }
 * // ]
 */
function parseInlineMarkdown(
  text: string,
  diffMark?: EDiffMark,
): JSONContent[] {
  const nodes: JSONContent[] = [];
  const diffMarks = diffMark ? [{ type: diffMark }] : [];
  let lastIndex = 0;
  let match;

  while ((match = BOLD_PATTERN.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(createTextNode(text.slice(lastIndex, match.index), diffMarks));
    }
    nodes.push(createTextNode(match[1], [{ type: 'bold' }, ...diffMarks]));
    lastIndex = match.index + match[0].length;
  }

  BOLD_PATTERN.lastIndex = 0;

  if (lastIndex < text.length) {
    nodes.push(createTextNode(text.slice(lastIndex), diffMarks));
  }

  return nodes;
}

// =============================================================================
// Diff to TipTap Conversion Helpers
// =============================================================================

/**
 * Char-level diff should only be used for pure additions or pure removals.
 *
 * @example
 * - "apple" vs "apples": only additions → char-level OK
 * - "walking" vs "walk": only removals → char-level OK
 * - "two" vs "four": both additions and removals → word-level
 * - "hello" vs "Hello": both additions and removals (remove h, add H) → word-level
 */
function isReplacement(changes: Change[]): boolean {
  let hasAdditions = false;
  let hasRemovals = false;

  for (const change of changes) {
    if (change.added) hasAdditions = true;
    else if (change.removed) hasRemovals = true;

    if (hasAdditions && hasRemovals) return true;
  }

  return false;
}

/**
 * Checks if two strings share a common prefix or suffix.
 *
 * If strings share no common characters at the start or end, they are
 * likely completely different words and char-level diff would be noisy.
 *
 * @param a - First string to compare
 * @param b - Second string to compare
 * @returns True if strings share at least one common character at start or end
 *
 * @example
 * hasCommonPrefixOrSuffix('apple', 'apples') // true - common prefix 'apple'
 * hasCommonPrefixOrSuffix('walking', 'talking') // true - common suffix 'alking'
 * hasCommonPrefixOrSuffix('cat', 'dog') // false - no common prefix or suffix
 */
function hasCommonPrefixOrSuffix(a: string, b: string): boolean {
  const aTrimmed = a.trim();
  const bTrimmed = b.trim();
  if (!aTrimmed || !bTrimmed) return false;

  return (
    aTrimmed[0] === bTrimmed[0] || aTrimmed.slice(-1) === bTrimmed.slice(-1)
  );
}

/**
 * Converts an array of diff changes to TipTap text nodes with appropriate marks.
 *
 * Each change from the diff library is converted to one or more TipTap text nodes.
 * Unchanged text gets no diff marks, added text gets ADDED marks, removed text gets DELETED marks.
 * Inline markdown (bold) is preserved within each change.
 *
 * @param changes - Array of Change objects from the diff library
 * @returns Array of TipTap text nodes ready for rendering
 *
 */
function changesToNodes(changes: Change[]): JSONContent[] {
  return changes.flatMap(change => {
    if (!change.value) return [];
    const mark = change.removed
      ? EDiffMark.DELETED
      : change.added
        ? EDiffMark.ADDED
        : undefined;
    return parseInlineMarkdown(change.value, mark);
  });
}

/**
 * Computes diff and converts to TipTap text nodes.
 * Applies word-level diff first, then for each word replacement,
 * decides whether to use char-level or word-level.
 *
 * Optimization: skips diffChars if words share no common prefix/suffix.
 *
 * @example
 * - "Add" → "Adds": common prefix "Add" → char-level: Add[+s]
 * - "two" → "four": no common prefix/suffix → word-level: [-two][+four]
 */
function computeCharDiffNodes(oldStr: string, newStr: string): JSONContent[] {
  const wordChanges = diffWords(oldStr, newStr);
  const result: JSONContent[] = [];

  for (let i = 0; i < wordChanges.length; i++) {
    const change = wordChanges[i];
    const nextChange = wordChanges[i + 1];

    // Unchanged text
    if (!change.added && !change.removed) {
      result.push(...parseInlineMarkdown(change.value));
      continue;
    }

    // Word replacement: removed followed by added
    if (change.removed && nextChange?.added) {
      // Quick check: only use char-level diff if there is a common prefix or suffix
      if (hasCommonPrefixOrSuffix(change.value, nextChange.value)) {
        const charChanges = diffChars(change.value, nextChange.value);

        // If the char changes are a replacement, use word-level diff i.e. "test" -> "toast" would not be t[-est][+oast] but [-test][+toast]
        if (isReplacement(charChanges)) {
          result.push(...parseInlineMarkdown(change.value, EDiffMark.DELETED));
          result.push(
            ...parseInlineMarkdown(nextChange.value, EDiffMark.ADDED),
          );
        } else {
          result.push(...changesToNodes(charChanges));
        }
      } else {
        // No common prefix/suffix = definitely a replacement
        result.push(...parseInlineMarkdown(change.value, EDiffMark.DELETED));
        result.push(...parseInlineMarkdown(nextChange.value, EDiffMark.ADDED));
      }

      i++; // Skip next change since we processed it
      continue;
    }

    // Single addition or removal
    const mark = change.added ? EDiffMark.ADDED : EDiffMark.DELETED;
    result.push(...parseInlineMarkdown(change.value, mark));
  }

  return result;
}

/**
 * Gets the appropriate TipTap content nodes based on the diff status.
 *
 * Routes to the correct rendering strategy based on how the block changed:
 * - UNCHANGED: Render new content without diff marks
 * - ADDED: Render new content with addition marks
 * - REMOVED: Render old content with deletion marks
 * - MODIFIED: Compute character-level diff between old and new
 *
 * @param status - The diff status of the block
 * @param oldContent - The original content (used for REMOVED and MODIFIED)
 * @param newContent - The new content (used for UNCHANGED, ADDED, and MODIFIED)
 * @returns Array of TipTap text nodes with appropriate formatting and diff marks
 */
function getContentForStatus(
  status: EDiffStatus,
  oldContent: string,
  newContent: string,
): JSONContent[] {
  switch (status) {
    case EDiffStatus.UNCHANGED:
      return parseInlineMarkdown(newContent);
    case EDiffStatus.ADDED:
      return parseInlineMarkdown(newContent, EDiffMark.ADDED);
    case EDiffStatus.REMOVED:
      return parseInlineMarkdown(oldContent, EDiffMark.DELETED);
    case EDiffStatus.MODIFIED:
      return computeCharDiffNodes(oldContent, newContent);
    default:
      return [];
  }
}

/** Ensures content array is non-empty for TipTap. */
function ensureContent(nodes: JSONContent[]): JSONContent[] {
  return nodes.length > 0 ? nodes : [createTextNode('')];
}

/**
 * Type guard that checks if a block type is a list type.
 *
 * List blocks require special handling in TipTap because they must be
 * wrapped in a parent list node (orderedList or bulletList) with listItem children.
 *
 * @param blockType - The block type to check
 * @returns True if the block type is ORDERED_LIST or UNORDERED_LIST
 */
function isListBlockType(
  blockType: ECodeBlockType,
): blockType is ECodeBlockType.ORDERED_LIST | ECodeBlockType.UNORDERED_LIST {
  return (
    blockType === ECodeBlockType.ORDERED_LIST ||
    blockType === ECodeBlockType.UNORDERED_LIST
  );
}

/**
 * Creates a TipTap node from a block diff.
 *
 * Converts a single IBlockDiff into the appropriate TipTap JSONContent structure.
 * - Headings: Creates heading node with level attribute
 * - Paragraphs: Creates paragraph node
 * - Lists: Creates listItem node (to be wrapped by parent list later)
 *
 * The content with appropriate diff marks applied for additions, deletions, and modifications.
 *
 * @param diff - The block diff containing status, type, and content
 * @returns A TipTap JSONContent node, or null if the diff is invalid
 */
function createDiffNode(diff: IBlockDiff): JSONContent | null {
  const { status, blockType, oldContent = '', newContent = '' } = diff;
  const content = ensureContent(
    getContentForStatus(status, oldContent, newContent),
  );

  if (isListBlockType(blockType)) {
    return { type: 'listItem', content: [{ type: 'paragraph', content }] };
  }

  const nodeType =
    BLOCK_TYPE_TO_NODE[
      blockType as ECodeBlockType.HEADING | ECodeBlockType.PARAGRAPH
    ];
  const attrs =
    blockType === ECodeBlockType.HEADING && diff.level
      ? { level: diff.level }
      : undefined;

  return { type: nodeType, attrs, content };
}

// =============================================================================
// Exported Functions
// =============================================================================

/**
 * Converts structured diff results to TipTap JSON content.
 * Nests unordered items under the preceding ordered item.
 *
 * @param diffs - Array of block diffs from computeStructuredDiff
 * @returns TipTap JSONContent document
 */
export function diffToTipTapContent(diffs: IBlockDiff[]): JSONContent {
  const content: JSONContent[] = [];
  let orderedItems: JSONContent[] = []; // Accumulates ordered list items (1. 2. 3.)
  let unorderedItems: JSONContent[] = []; // Accumulates unordered items to nest under last ordered

  // Flushes accumulated lists to content
  const flush = () => {
    // Nest pending unordered items under last ordered item
    if (unorderedItems.length && orderedItems.length) {
      const lastItem = orderedItems.at(-1)!;
      lastItem.content!.push({ type: 'bulletList', content: unorderedItems });
      // Add blank paragraph after nested list for spacing
      lastItem.content!.push({ type: 'paragraph' });
      unorderedItems = [];
    }
    // Flush ordered list
    if (orderedItems.length) {
      content.push({ type: 'orderedList', content: orderedItems });
      orderedItems = [];
    }
    // Flush orphan unordered items (no preceding ordered item)
    if (unorderedItems.length) {
      content.push({ type: 'bulletList', content: unorderedItems });
      unorderedItems = [];
    }
  };

  for (const diff of diffs) {
    const node = createDiffNode(diff);
    if (!node) continue;

    if (diff.blockType === ECodeBlockType.ORDERED_LIST) {
      // Before adding new ordered item, nest any pending unordered items under previous ordered
      if (unorderedItems.length && orderedItems.length) {
        const lastItem = orderedItems.at(-1)!;
        lastItem.content!.push({ type: 'bulletList', content: unorderedItems });
        // Add blank paragraph after nested list for spacing
        lastItem.content!.push({ type: 'paragraph' });
        unorderedItems = [];
      }
      orderedItems.push(node);
    } else if (diff.blockType === ECodeBlockType.UNORDERED_LIST) {
      // Accumulate unordered items to nest later
      unorderedItems.push(node);
    } else {
      // Non-list block: flush all lists and add the block
      flush();
      content.push(node);
    }
  }

  flush();
  return { type: 'doc', content };
}

/**
 * Computes diff between two markdown documents and returns TipTap content.
 *
 * This is the main entry point that combines structured diff computation
 * with TipTap conversion.
 *
 * @param oldCode - The old version of the markdown code
 * @param newCode - The new version of the markdown code
 * @returns TipTap JSONContent document with diff marks
 */
export function computeDiffContent(
  oldCode: string,
  newCode: string,
): JSONContent {
  return diffToTipTapContent(computeStructuredDiff(oldCode, newCode));
}
